import axios from 'axios';
import { auth } from '../config/firebase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Chaque requête porte l'ID token Firebase de l'utilisateur connecté, comme
 * le fait l'application mobile. Le SDK rafraîchit le jeton tout seul :
 * `getIdToken()` renvoie toujours un jeton valide, il suffit de le demander
 * juste avant l'envoi.
 */
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      config.headers.Authorization = `Bearer ${await user.getIdToken()}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 401 = jeton absent, expiré ou révoqué : on ferme la session côté client.
    // La redirection est laissée aux composants, sauf sur les pages privées.
    if (error.response?.status === 401 && auth.currentUser) {
      await auth.signOut();
    }
    return Promise.reject(error);
  }
);

/** Configuration publique : inscriptions ouvertes, mode maintenance. */
export const configAPI = {
  get: () => api.get('/config'),
};

/**
 * Parcours d'inscription, identique à celui de l'application :
 * numéro → canal → code → email + mot de passe → profil.
 * Le canal SMS est géré directement par le SDK Firebase (voir AuthContext).
 */
export const authAPI = {
  checkPhone: (phone) => api.get('/auth/phone', { params: { phone } }),
  checkEmail: (email) => api.get('/auth/exists', { params: { email } }),
  sendWhatsappOtp: (phone) => api.post('/auth/otp/whatsapp/send', { phone }),
  verifyWhatsappOtp: (phone, code) => api.post('/auth/otp/whatsapp/verify', { phone, code }),
  createProfile: (data) => api.post('/auth/profile', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.patch('/auth/me', data),
};

/** Catégories de prestations (34, alignées sur l'appli). */
export const categoriesAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getById: (id) => api.get(`/categories/${id}`),
};

/** Fiches des prestataires. */
export const smartsAPI = {
  getAll: (params) => api.get('/smarts', { params }),
  getById: (id) => api.get(`/smarts/${id}`),
  getReviews: (id) => api.get(`/smarts/${id}/reviews`),
};

/** Réservations — collection `reservations`, partagée avec l'appli. */
export const bookingsAPI = {
  create: (data) => api.post('/bookings', data),
  getAll: () => api.get('/bookings'),
  getById: (id) => api.get(`/bookings/${id}`),
  updateStatus: (id, statut) => api.patch(`/bookings/${id}`, { statut }),
};

export const reviewsAPI = {
  create: (data) => api.post('/reviews', data),
};

/** Candidatures Smart — collection `candidatures`. */
export const applicationsAPI = {
  submit: (data) => api.post('/applications', data),
  getMy: () => api.get('/applications/my'),
  updateSmartProfile: (data) => api.patch('/applications/my/smart', data),
};

export const contactAPI = {
  send: (data) => api.post('/contact', data),
};

export default api;
